<script setup lang="ts">
import { ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import type { ConfigModule } from '~/types/config';

interface Props {
  visible: boolean;
  deletions: ConfigModule[];
}

const props = defineProps<Props>();

const emit = defineEmits<{
  'update:visible': [visible: boolean];
  'undo-deletion': [categoryXPath: string];
  'restore-all-deletions': [];
  'navigate-to-category': [{ moduleId: number; xPath: string }];
}>();

const { t } = useI18n();

// Local state for the dialog visibility
const dialogVisible = ref(props.visible);

// Local state for accordion functionality
const expandedModules = ref<Set<number>>(new Set());

// Synchronize local state with the prop
watch(() => props.visible, (newValue) => {
  dialogVisible.value = newValue;
});

// Synchronize changes back to the parent
watch(dialogVisible, (newValue) => {
  if (newValue !== props.visible) {
    emit('update:visible', newValue);
  }
});

// Function to navigate to a specific category within a module
const navigateToCategory = (moduleId: number, xPath: string) => {
  dialogVisible.value = false;
  emit('navigate-to-category', { moduleId, xPath });
};

// Function to undo deletion of a category
const undoDeletion = (categoryXPath: string) => {
  emit('undo-deletion', categoryXPath);
};

// Function to restore all deletions
const restoreAllDeletions = () => {
  emit('restore-all-deletions');
};

// Function to toggle module expansion
const toggleModuleExpansion = (moduleId: number) => {
  if (expandedModules.value.has(moduleId)) {
    expandedModules.value.delete(moduleId);
  } else {
    expandedModules.value.add(moduleId);
  }
};

// Initialize all modules as expanded
watch(() => props.deletions, (newDeletions) => {
  if (newDeletions && newDeletions.length > 0) {
    expandedModules.value = new Set(newDeletions.map(module => module.moduleId));
  }
}, { immediate: true });
</script>

<template>
  <Dialog
    :visible="dialogVisible"
    @update:visible="dialogVisible = $event"
    :header="$t('common.dialogs.deletions.title')"
    modal
    dismissable-mask
    class="w-full"
    :style="{ maxWidth: '650px', width: '85vw' }"
  >
    <div class="p-3">
      <Message
        v-if="deletions.length === 0"
        severity="info"
        :closable="false"
        class="mb-3"
      >
        {{ $t('common.dialogs.deletions.noDeletions') }}
      </Message>

      <div v-else>
        <Message
          severity="warn"
          :closable="false"
          class="mb-3"
        >
          {{ deletions.length === 1 ? 
            $t('common.dialogs.deletions.warning', { count: deletions.length }) :
            $t('common.dialogs.deletions.warningPlural', { count: deletions.length })
          }}
        </Message>

        <!-- Restore All Button -->
        <div class="flex justify-content-end mb-3">
          <Button
            :label="$t('common.dialogs.deletions.restoreAll')"
            severity="secondary"
            type="button"
            icon="pi pi-undo"
            size="small"
            @click="restoreAllDeletions"
            class="w-auto"
          />
        </div>

        <div class="max-h-30rem overflow-auto">
          <div v-for="module in deletions" :key="module.moduleId" class="mb-3">
            <div
              class="module-header cursor-pointer p-3 bg-primary-50 border-1 border-primary-200 border-round hover:bg-primary-100 transition-colors transition-duration-150"
              @click="toggleModuleExpansion(module.moduleId)"
            >
              <div class="flex align-items-center justify-content-between">
                <div class="flex align-items-center">
                  <i
                    class="mr-2 transition-transform transition-duration-150"
                    :class="expandedModules.has(module.moduleId) ? 'pi pi-chevron-down' : 'pi pi-chevron-right'"
                  ></i>
                  <i class="pi pi-folder-open mr-2 text-primary-700"></i>
                  <h5 class="text-base font-semibold text-primary-800 m-0">
                    {{ module.translation }}
                  </h5>
                </div>
                <Badge
                  :value="Object.keys(module.categories).length.toString()"
                  severity="secondary"
                  class="ml-2"
                />
              </div>
            </div>

            <div
              v-if="expandedModules.has(module.moduleId)"
              class="category-list border-1 border-l-primary-200 border-r-primary-200 border-b-primary-200 border-round-bottom overflow-hidden"
            >
              <div
                v-for="(category, index) in Object.values(module.categories)"
                :key="category.xpath || category.categoryName"
                class="category-item flex align-items-center justify-content-between p-3 hover:bg-gray-50 transition-colors transition-duration-150 bg-white"
                :class="{ 'border-bottom-1 border-gray-200': index < Object.values(module.categories).length - 1 }"
              >
                <div class="flex align-items-center flex-1">
                  <i class="pi pi-minus text-orange-500 mr-3 text-lg"/>
                  <span class="text-gray-800 font-medium">
                    {{ category.translation || category.categoryName }}
                  </span>
                </div>
                <div class="flex align-items-center gap-2">
                  <Button
                    severity="secondary"
                    type="button"
                    icon="pi pi-undo"
                    size="small"
                    @click.stop="undoDeletion(category.xpath)"
                    v-tooltip.top="$t('common.actions.undo')"
                  />
                  <Button
                    severity="info"
                    type="button"
                    icon="pi pi-external-link"
                    size="small"
                    @click.stop="navigateToCategory(module.moduleId, category.xpath)"
                    v-tooltip.top="$t('common.dialogs.changes.goToField')"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </Dialog>
</template>